// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Loader for the federal AEC dataset generated by pipeline/collect.mjs.
// The JSON is refreshed yearly (the AEC publishes annual returns each
// February) by .github/workflows/data-pipeline.yml and served as a static
// asset from public/data/donations.json.

import type { Donation, DonorType, DonationCategory } from './donations';
import { PARTIES, type PartyCode } from './parties';

export type FederalMeta = {
  source: string;
  sourceUrl: string;
  generatedAt: string;
  financialYears: string[];
  recordCount: number;
  totalAmount: number;
};

export type FederalData = {
  donations: Donation[];
  meta: FederalMeta | null;
};

const DONOR_TYPES: ReadonlySet<string> = new Set([
  'Individual', 'Corporation', 'Union', 'Associated entity', 'Foundation', 'Industry body', 'Third party',
]);
const CATEGORIES: ReadonlySet<string> = new Set(['Cash', 'In-kind', 'Receipt', 'Subscription']);

/**
 * Sanitize one raw JSON record into a Donation. Returns null for records
 * that can't be trusted (missing fields, bad amounts). Unknown party codes
 * fall back to OTH so a register-side change can never crash the charts.
 */
function toDonation(raw: unknown, index: number): Donation | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const donor = typeof r.donor === 'string' ? r.donor.trim() : '';
  const recipient = typeof r.recipient === 'string' ? r.recipient.trim() : '';
  const amount = typeof r.amount === 'number' && Number.isFinite(r.amount) ? r.amount : 0;
  const fy = typeof r.fy === 'string' && /^\d{4}-\d{2}$/.test(r.fy) ? r.fy : '';
  if (!donor || !recipient || !fy || amount <= 0) return null;

  const party = typeof r.recipientParty === 'string' && r.recipientParty in PARTIES
    ? (r.recipientParty as PartyCode)
    : 'OTH';
  const donorType = typeof r.donorType === 'string' && DONOR_TYPES.has(r.donorType)
    ? (r.donorType as DonorType)
    : 'Corporation';
  const category = typeof r.category === 'string' && CATEGORIES.has(r.category)
    ? (r.category as DonationCategory)
    : 'Cash';

  return {
    id: typeof r.id === 'string' ? r.id : `aec-${index}`,
    donor,
    donorType,
    recipient,
    recipientParty: party,
    jurisdiction: 'CTH',
    amount,
    fy,
    category,
  };
}

/**
 * Fetch and validate the federal dataset. Never throws: on any failure it
 * resolves to an empty dataset so the app can fall back to the curated
 * state snapshot (with a visible notice).
 */
export async function loadFederalDonations(): Promise<FederalData> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}data/donations.json`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const payload = (await res.json()) as { meta?: FederalMeta; donations?: unknown[] };
    if (!Array.isArray(payload.donations)) throw new Error('malformed payload: no donations array');

    const donations: Donation[] = [];
    payload.donations.forEach((raw, i) => {
      const d = toDonation(raw, i);
      if (d) donations.push(d);
    });
    if (donations.length === 0) throw new Error('payload contained no valid records');

    return { donations, meta: payload.meta ?? null };
  } catch (err) {
    console.error('Failed to load federal donation data:', err);
    return { donations: [], meta: null };
  }
}
